import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  ActivityIndicator,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { cookFacingError } from '../../src/i18n/cookFacingError';
import Feather from '@expo/vector-icons/Feather';
import * as ImagePicker from 'expo-image-picker';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius } from '../../src/styles/theme';
import { AppDispatch, RootState } from '../../src/redux/store';
import { isAdminUser } from '../../src/utils/isAdmin';
import { ReviewRow } from '../../src/components/recipes/review/ReviewRow';
import { ReviewLegend, ReviewLoopStrip } from '../../src/components/recipes/review/ReviewLoopStrip';
import { needsSuggestion, rowDecisions } from '../../src/utils/recipeMetadataReview';
import {
  filterOf,
  REVIEW_FILTERS,
  REVIEW_SOURCE_FILTERS,
  withState,
  type ReviewCounts,
  type ReviewFilter,
  type ReviewRow as ReviewRowData,
  type ReviewSourceFilter,
} from '../../src/utils/recipeReviewQueue';
import {
  addPhoto,
  fetchLoopStats,
  fetchReviewPage,
  glanceConfirm,
  requestSuggestions,
  submitReview,
  SUGGEST_BATCH_SIZE,
} from '../../src/services/recipeMetadataService';
import type { MetadataDecisions } from '../../src/types/recipeMetadata';
import type { LoopStats } from '../../src/utils/recipeMetadataScoring';
import {
  publishRecipes,
  refreshLibraryAfterPublish,
  setReviewStatus,
} from '../../src/services/importReviewService';
import { apiPost } from '../../src/services/apiClient';
import unifiedRecipeService from '../../src/services/unifiedRecipeService';
import { invalidateRecipeIndex } from '../../src/redux/recipeIndexSlice';

/**
 * Review — admin only. One list for every recipe whose tags are not settled:
 * new collection imports, the household's library (Chrome, email, app
 * imports) and the shared catalogue.
 *
 * Each row shows cuisine, dish type and diet in the state that says who
 * settled them. Accept is locked while the AI is unsure of a field or has
 * nothing; tapping that field confirms or corrects it. Accept on an import
 * moves it to Ready to publish (`approved`); on any other row it is done.
 * "These N look right" confirms, at a glance, rows the AI is sure of with
 * nothing to warn about. Every answer writes a `metadataLabels` row, which is
 * what `npm run eval:recipe-metadata` scores and what primes the next
 * suggestion; the strip at the top shows that score.
 */
export default function ReviewScreen() {
  const router = useRouter();
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.auth.user);
  const householdId = useSelector((state: RootState) => state.auth.householdId);
  const isAdmin = isAdminUser(user?.email);

  const [filter, setFilter] = useState<ReviewFilter>('to-check');
  const [source, setSource] = useState<ReviewSourceFilter>('all');
  const [rows, setRows] = useState<ReviewRowData[]>([]);
  const [counts, setCounts] = useState<ReviewCounts | null>(null);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [suggesting, setSuggesting] = useState<Set<string>>(new Set());
  const [stats, setStats] = useState<LoopStats | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  /** Rows already sent to the model this visit, so a failure is not retried in a loop. */
  const attempted = useRef<Set<string>>(new Set());
  /** A catalogue or library write makes the persisted recipe index stale. */
  const wroteLibrary = useRef(false);

  const keyOf = (row: Pick<ReviewRowData, 'id' | 'scope'>) => `${row.scope}:${row.id}`;

  const load = useCallback(async (append = false, offset = 0) => {
    if (!user?.uid || !isAdmin) {
      setLoading(false);
      return;
    }
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const page = await fetchReviewPage({ filter, source, offset });
      setRows((prev) => (append ? [...prev, ...page.rows] : page.rows));
      setCounts(page.counts);
      setNextOffset(page.nextOffset);
    } catch (error) {
      console.error('[import-review] load failed', error);
      setMessage(t('importReview.loadFailed'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [user?.uid, isAdmin, filter, source, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isAdmin || !user?.uid) return;
    fetchLoopStats().then(setStats).catch(() => setStats(null));
  }, [isAdmin, user?.uid]);

  // Leaving the screen after a write: the library's cached index would still
  // show the old tags until it expires.
  useEffect(() => () => {
    if (!wroteLibrary.current) return;
    unifiedRecipeService.invalidateCache();
    dispatch(invalidateRecipeIndex());
  }, [dispatch]);

  const patchRow = useCallback((key: string, changes: Partial<ReviewRowData>) => {
    setRows((prev) => prev.map((r) => (keyOf(r) === key ? withState({ ...r, ...changes }) : r)));
  }, []);

  const rowsRef = useRef<ReviewRowData[]>([]);
  rowsRef.current = rows;

  /** Rows that left this filter (accepted, confirmed, published, rejected), and the counts with them. */
  const dropRows = useCallback((keys: string[], to?: ReviewFilter) => {
    const gone = new Set(keys);
    const leaving = rowsRef.current.filter((r) => gone.has(keyOf(r)));
    if (leaving.length === 0) return;
    setRows((prev) => prev.filter((r) => !gone.has(keyOf(r))));
    setCounts((c) => {
      if (!c) return c;
      const filters = { ...c.filters, [filter]: Math.max(0, c.filters[filter] - leaving.length) };
      if (to) filters[to] += leaving.length;
      const sources = { ...c.sources, all: Math.max(0, c.sources.all - leaving.length) };
      for (const r of leaving) sources[r.source] = Math.max(0, sources[r.source] - 1);
      return { filters, sources };
    });
  }, [filter]);

  // Rows arrive pre-filled: any row on screen without a suggestion is sent to
  // the model, a few at a time, each row filling as its answer lands.
  useEffect(() => {
    if (loading) return;
    const fresh = rows.filter((r) => needsSuggestion(r) && !attempted.current.has(keyOf(r)));
    if (fresh.length === 0) return;
    fresh.forEach((r) => attempted.current.add(keyOf(r)));
    setSuggesting((prev) => new Set([...prev, ...fresh.map(keyOf)]));
    (async () => {
      for (let i = 0; i < fresh.length; i += SUGGEST_BATCH_SIZE) {
        const chunk = fresh.slice(i, i + SUGGEST_BATCH_SIZE);
        try {
          const suggestions = await requestSuggestions(chunk);
          chunk.forEach((r) => {
            if (suggestions[r.id]) patchRow(keyOf(r), { metadataSuggestion: suggestions[r.id] });
          });
        } catch (error) {
          console.error('[import-review] suggest failed', error);
          setMessage(t('recipeMetadata.suggestFailed'));
        } finally {
          setSuggesting((prev) => {
            const next = new Set(prev);
            chunk.forEach((r) => next.delete(keyOf(r)));
            return next;
          });
        }
      }
    })();
  }, [loading, rows, patchRow, t]);

  const handleDecide = useCallback(async (row: ReviewRowData, decisions: MetadataDecisions, options?: { timeTyped?: boolean }) => {
    try {
      const { update } = await submitReview(row, decisions, options);
      if (row.scope !== 'household' || row.source !== 'import') wroteLibrary.current = true;
      // The row stays where it is until Accept: a row that jumped filters
      // under the reviewer's finger would be impossible to finish.
      patchRow(keyOf(row), update as Partial<ReviewRowData>);
    } catch (error) {
      console.error('[import-review] review failed', error);
      setMessage(t('recipeMetadata.reviewFailed'));
    }
  }, [patchRow, t]);

  const handleAccept = useCallback(async (row: ReviewRowData) => {
    try {
      const { update } = await submitReview(row, rowDecisions(row), { approve: true });
      if (row.source !== 'import') wroteLibrary.current = true;
      const next = withState({ ...row, ...(update as Partial<ReviewRowData>) });
      dropRows([keyOf(row)], filterOf(next.state));
    } catch (error) {
      console.error('[import-review] accept failed', error);
      setMessage(t('recipeMetadata.reviewFailed'));
    }
  }, [dropRows, t]);

  const handleGlance = useCallback(async () => {
    const shown = rows.filter((r) => r.state === 'looks-right');
    if (shown.length === 0) return;
    setBulkBusy(true);
    setMessage(null);
    try {
      const result = await glanceConfirm(shown);
      wroteLibrary.current = true;
      const confirmed = new Set(result.confirmed.map((c) => c.id));
      // Imports go on to Ready to publish; everything else is done.
      const toPublish = shown.filter((r) => confirmed.has(r.id) && r.source === 'import').map(keyOf);
      const toDone = shown.filter((r) => confirmed.has(r.id) && r.source !== 'import').map(keyOf);
      dropRows(toPublish, 'ready-to-publish');
      dropRows(toDone, 'done');
      setMessage([
        t('importReview.glanceDone', { count: result.confirmed.length }),
        result.skipped.length ? t('importReview.glanceSkipped', { count: result.skipped.length }) : null,
      ].filter(Boolean).join(' · '));
    } catch (error) {
      console.error('[import-review] glance confirm failed', error);
      setMessage(t('recipeMetadata.reviewFailed'));
    } finally {
      setBulkBusy(false);
    }
  }, [rows, dropRows, t]);

  const handlePublish = useCallback(async () => {
    const ready = rows.filter((r) => r.state === 'ready-to-publish');
    if (ready.length === 0) return;
    setBulkBusy(true);
    setMessage(null);
    try {
      const result = await publishRecipes(ready.map((r) => r.id));
      refreshLibraryAfterPublish(result, dispatch);
      const published = new Set(result.published.map((p) => p.id));
      dropRows(ready.filter((r) => published.has(r.id)).map(keyOf));
      setMessage(
        result.skipped.length > 0
          ? t('importReview.publishPartial', { published: result.published.length, skipped: result.skipped.length })
          : t('importReview.publishSuccess', { count: result.published.length }),
      );
    } catch (error: any) {
      console.error('[import-review] publish failed', error);
      setMessage(cookFacingError(error, t, 'importReview.publishFailed'));
    } finally {
      setBulkBusy(false);
    }
  }, [rows, dispatch, dropRows, t]);

  const handleReject = useCallback(async (row: ReviewRowData) => {
    if (!user?.uid) return;
    await setReviewStatus(user.uid, householdId, row.id, 'rejected');
    dropRows([keyOf(row)]);
  }, [user?.uid, householdId, dropRows]);

  const handleRefetch = useCallback(async (row: ReviewRowData) => {
    const key = keyOf(row);
    try {
      const data = await apiPost<{ fieldsUpdated?: string[]; values?: Record<string, unknown>; message?: string }>(
        '/api/recipes/enrich',
        { recipeId: row.id, scope: row.scope },
      );
      const fields = data.fieldsUpdated ?? [];
      let note: string;
      if (fields.length > 0) note = t('importReview.refetchFound', { fields: fields.join(', ') });
      else if (/no sourceUrl/i.test(data.message ?? '')) note = t('importReview.refetchNoSource');
      else if (/could not fetch|not safe/i.test(data.message ?? '')) note = t('importReview.refetchUnreadable');
      else note = t('importReview.refetchNothing');
      setNotes((n) => ({ ...n, [key]: note }));
      if (fields.length > 0) {
        if (row.source !== 'import') wroteLibrary.current = true;
        const values = data.values ?? {};
        const gaps = row.gaps.filter((g) =>
          !(g === 'image' && values.image)
          && !(g === 'steps' && Array.isArray(values.instructions) && values.instructions.length)
          && !(g === 'ingredients' && Array.isArray(values.extendedIngredients) && values.extendedIngredients.length));
        patchRow(key, { ...(values as Partial<ReviewRowData>), gaps });
      }
    } catch (error) {
      console.error('[import-review] re-fetch failed', error);
      setNotes((n) => ({ ...n, [key]: t('importReview.refetchUnreadable') }));
    }
  }, [patchRow, t]);

  const handleAddPhoto = useCallback(async (row: ReviewRowData) => {
    if (!user?.uid) return;
    const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ['images'], quality: 0.8 });
    if (result.canceled || !result.assets[0]) return;
    try {
      const url = await addPhoto(row, result.assets[0].uri, user.uid);
      if (!url) {
        setNotes((n) => ({ ...n, [keyOf(row)]: t('importReview.photoFailed') }));
        return;
      }
      if (row.source !== 'import') wroteLibrary.current = true;
      patchRow(keyOf(row), { image: url, gaps: row.gaps.filter((g) => g !== 'image') });
    } catch (error) {
      console.error('[import-review] add photo failed', error);
      setNotes((n) => ({ ...n, [keyOf(row)]: t('importReview.photoFailed') }));
    }
  }, [user?.uid, patchRow, t]);

  if (!isAdmin) {
    return (
      <View style={styles.centered}>
        <Feather name="lock" size={28} color={Paprika.textMuted} />
        <Text style={styles.emptyText}>{t('importReview.adminOnly')}</Text>
      </View>
    );
  }

  const glanceCount = filter === 'looks-right' ? rows.filter((r) => r.state === 'looks-right').length : 0;
  const publishCount = filter === 'ready-to-publish' ? rows.filter((r) => r.state === 'ready-to-publish').length : 0;

  return (
    <View style={styles.screen}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.back()} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
          <Feather name="chevron-left" size={24} color={Paprika.textPrimary} />
        </TouchableOpacity>
        <View style={styles.headerText}>
          <Text style={styles.screenTitle}>{t('importReview.title')}</Text>
          <Text style={styles.subtitle}>{t('importReview.subtitle')}</Text>
        </View>
      </View>

      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        <ReviewLoopStrip stats={stats} />

        <View style={styles.chips}>
          {REVIEW_FILTERS.map((f) => (
            <Chip
              key={f}
              testID={`review-filter-${f}`}
              active={filter === f}
              label={`${t(`importReview.filters.${f}`)}${counts ? ` · ${counts.filters[f]}` : ''}`}
              onPress={() => setFilter(f)}
            />
          ))}
        </View>
        <View style={styles.chips}>
          {REVIEW_SOURCE_FILTERS.map((s) => (
            <Chip
              key={s}
              testID={`review-source-${s}`}
              quiet
              active={source === s}
              label={`${t(`importReview.sources.${s}`)}${counts ? ` · ${counts.sources[s]}` : ''}`}
              onPress={() => setSource(s)}
            />
          ))}
        </View>
        <ReviewLegend />

        {message ? (
          <View style={styles.messageBar} testID="review-message">
            <Text style={styles.messageText}>{message}</Text>
          </View>
        ) : null}

        {filter === 'looks-right' && glanceCount > 0 ? (
          <Text style={styles.hint}>{t('importReview.glanceHint')}</Text>
        ) : null}

        {loading ? (
          <View style={styles.centered}>
            <ActivityIndicator color={Paprika.primary} />
          </View>
        ) : rows.length === 0 ? (
          <View style={styles.centered}>
            <Feather name="check-circle" size={28} color={Paprika.success} />
            <Text style={styles.emptyText}>{t(`importReview.emptyFilter.${filter}`)}</Text>
          </View>
        ) : (
          <>
            {rows.map((row) => (
              <ReviewRow
                key={keyOf(row)}
                row={row}
                suggesting={suggesting.has(keyOf(row))}
                note={notes[keyOf(row)]}
                onDecide={(decisions, options) => handleDecide(row, decisions, options)}
                onAccept={() => handleAccept(row)}
                onRefetch={() => handleRefetch(row)}
                onAddPhoto={() => handleAddPhoto(row)}
                onOpen={() => router.push(`/(app)/recipe-detail/${row.id}`)}
                // Editing a catalogue recipe from the app forks a private copy;
                // the chips, Re-fetch and Add photo are its repairs here.
                onEdit={row.scope === 'catalogue' ? undefined : () => router.push(`/(app)/recipe-detail/${row.id}?edit=true`)}
                onReject={row.source === 'import' ? () => handleReject(row) : undefined}
              />
            ))}
            {nextOffset != null ? (
              <TouchableOpacity
                testID="review-load-more"
                style={styles.loadMore}
                onPress={() => load(true, nextOffset)}
                disabled={loadingMore}
              >
                {loadingMore
                  ? <ActivityIndicator size="small" color={Paprika.primary} />
                  : <Text style={styles.loadMoreText}>{t('importReview.loadMore')}</Text>}
              </TouchableOpacity>
            ) : null}
          </>
        )}
      </ScrollView>

      {glanceCount > 0 || publishCount > 0 ? (
        <View style={styles.bottomBar}>
          <TouchableOpacity
            testID={glanceCount > 0 ? 'review-glance' : 'review-publish'}
            style={styles.bottomBtn}
            onPress={glanceCount > 0 ? handleGlance : handlePublish}
            disabled={bulkBusy}
            activeOpacity={0.85}
          >
            {bulkBusy ? (
              <ActivityIndicator color={Paprika.onPrimary} />
            ) : (
              <Text style={styles.bottomBtnText}>
                {glanceCount > 0
                  ? t('importReview.glance', { count: glanceCount })
                  : t('importReview.publishSelected', { count: publishCount })}
              </Text>
            )}
          </TouchableOpacity>
        </View>
      ) : null}
    </View>
  );
}

const Chip: React.FC<{ label: string; active: boolean; onPress: () => void; quiet?: boolean; testID?: string }> = ({
  label, active, onPress, quiet = false, testID,
}) => (
  <TouchableOpacity
    testID={testID}
    onPress={onPress}
    style={[styles.chip, quiet && styles.chipQuiet, active && (quiet ? styles.chipQuietActive : styles.chipActive)]}
    activeOpacity={0.8}
  >
    <Text style={[styles.chipText, active && (quiet ? styles.chipQuietActiveText : styles.chipActiveText)]}>{label}</Text>
  </TouchableOpacity>
);

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: PaprikaSpacing.md,
    paddingTop: PaprikaSpacing.md,
    paddingBottom: PaprikaSpacing.sm,
    gap: PaprikaSpacing.sm,
  },
  headerText: {
    flex: 1,
    gap: 2,
  },
  screenTitle: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 30,
    lineHeight: 36,
    color: Paprika.textPrimary,
  },
  subtitle: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: PaprikaSpacing.sm,
    paddingBottom: 112,
  },
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.md,
    paddingBottom: PaprikaSpacing.sm,
  },
  chip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: PaprikaRadius.xl,
    backgroundColor: Paprika.surfaceLow,
  },
  chipActive: {
    backgroundColor: Paprika.primary,
  },
  chipQuiet: {
    paddingVertical: 6,
    backgroundColor: 'transparent',
  },
  chipQuietActive: {
    backgroundColor: Paprika.brandSoft,
  },
  chipText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textSecondary,
  },
  chipActiveText: {
    color: Paprika.onPrimary,
  },
  chipQuietActiveText: {
    color: Paprika.primary,
  },
  hint: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    paddingHorizontal: PaprikaSpacing.md,
    paddingBottom: PaprikaSpacing.sm,
  },
  centered: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: PaprikaSpacing.sm,
    padding: PaprikaSpacing.xl,
  },
  emptyText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    textAlign: 'center',
  },
  messageBar: {
    marginHorizontal: PaprikaSpacing.md,
    marginBottom: PaprikaSpacing.sm,
    padding: PaprikaSpacing.sm,
    borderRadius: PaprikaRadius.sm,
    backgroundColor: Paprika.surfaceLow,
  },
  messageText: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
  },
  loadMore: {
    alignSelf: 'center',
    marginTop: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.lg,
    paddingVertical: 10,
    borderRadius: PaprikaRadius.xl,
    backgroundColor: Paprika.surfaceLow,
  },
  loadMoreText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.primary,
  },
  // Floats above the list; the only place a shadow is warranted on this screen.
  bottomBar: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    padding: PaprikaSpacing.md,
    backgroundColor: Paprika.surfaceLowest,
    ...Platform.select({
      ios: {
        shadowColor: 'rgba(82,68,60,1)',
        shadowOffset: { width: 0, height: -8 },
        shadowOpacity: 0.08,
        shadowRadius: 24,
      },
      android: { elevation: 8 },
      web: { boxShadow: '0 -8px 24px rgba(82,68,60,0.08)' },
    }),
  },
  bottomBtn: {
    backgroundColor: Paprika.primary,
    borderRadius: 24,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bottomBtnText: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.onPrimary,
  },
});
